// Real backend implementation of the API contract. Cookie session, JSON, /api prefix.

import type { ChatEvent } from "../types";
import { ApiError, type Api } from "./contract";
import { t } from "../i18n/sl";

const BASE = "/api";

/** True for a server message meant for people (not a bare status text such as "Too Many Requests"). */
function humanDetail(detail: string | null, res: Response): boolean {
  if (!detail) return false;
  if (detail === res.statusText) return false;
  return !/^(too many requests|rate limit|internal server error|bad gateway|service unavailable|gateway timeout|http \d+)/i.test(detail.trim());
}

/** Turns a failed response into an ApiError with a calm Slovenian message.
 *  429: daily AI budget or rate limit. 5xx without a JSON detail: the function timed out or crashed. */
export async function errorFromResponse(res: Response): Promise<ApiError> {
  let detail: string | null = null;
  let code: string | undefined;
  try {
    const data = await res.json();
    if (data && typeof data.code === "string") code = data.code;
    if (data && typeof data.detail === "string") detail = data.detail;
    else if (data && Array.isArray(data.detail)) detail = data.detail.map((d: { msg?: string }) => d.msg).join("; ");
    else if (data && typeof data.error === "string") detail = data.error;
  } catch {
    /* not JSON */
  }
  if (res.status === 429) {
    if (humanDetail(detail, res)) return new ApiError(429, detail!);
    const sec = Number(res.headers.get("retry-after"));
    const min = Number.isFinite(sec) && sec > 0 ? Math.ceil(sec / 60) : 0;
    return new ApiError(429, min > 0 && min <= 120 ? t.errors.rateLimitedIn(min) : t.errors.rateLimited);
  }
  if (res.status >= 500 && !humanDetail(detail, res)) return new ApiError(res.status, t.errors.serverBusy, code);
  return new ApiError(res.status, detail || res.statusText || `HTTP ${res.status}`, code);
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const init: RequestInit = { method, credentials: "include", headers: {}, signal };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(BASE + path, init);
  if (!res.ok) throw await errorFromResponse(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    // e.g. the SPA fallback answered with index.html because the API route is missing
    throw new ApiError(502, t.errors.serverBusy);
  }
}

const get = <T>(p: string, signal?: AbortSignal) => request<T>("GET", p, undefined, signal);
const post = <T>(p: string, b?: unknown) => request<T>("POST", p, b ?? {});
const patch = <T>(p: string, b: unknown) => request<T>("PATCH", p, b);
const enc = encodeURIComponent;

/** Parses a text/event-stream body and calls onEvent for each JSON `data:` line. */
export async function readSse(
  res: Response,
  onEvent: (e: ChatEvent) => void,
): Promise<void> {
  if (!res.body) throw new ApiError(res.status, t.errors.serverBusy);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawDone = false;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    // events are separated by a blank line; we also accept single data lines
    while ((idx = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, idx).replace(/\r$/, "");
      buffer = buffer.slice(idx + 1);
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      try {
        const ev = JSON.parse(payload) as ChatEvent;
        if (ev.type === "done") sawDone = true;
        onEvent(ev);
      } catch {
        /* ignore malformed line */
      }
    }
  }
  if (!sawDone) onEvent({ type: "done" });
}

export const httpApi: Api = {
  me: () => get("/me"),
  authRequest: (email, next) => post("/auth/request", { email, next }),
  authRedeem: (token) => post("/auth/redeem", { token }),
  logout: () => post("/auth/logout"),

  orgSuggest: (domain) => post("/org/suggest", { domain }),
  orgSuggestStatus: (jobId) => get(`/org/suggest/${enc(jobId)}`),
  orgCreate: (profile) => post("/org", { profile }),
  orgPatch: (p) => patch("/org", { patch: p }),
  rotateToken: (kind) => post("/org/tokens/rotate", { kind }),
  deleteOrg: (confirm_name) => request("DELETE", "/org", { confirm_name }),
  overview: () => get("/org/overview"),

  catalog: () => get("/catalog"),
  rulesSummary: () => get("/rules/summary"),

  systems: () => get("/systems"),
  createSystem: (system) => post("/systems", { system }),
  systemsFromText: (text) => post("/systems/from-text", { text }),
  systemsFromCatalog: (picks) => post("/systems/from-catalog", { picks }),
  importSystems: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request("POST", "/systems/import", fd);
  },
  importStatus: (jobId, signal) => get(`/systems/import/${enc(jobId)}`, signal),
  importCancel: (jobId) => post(`/systems/import/${enc(jobId)}/cancel`),
  getSystem: (id) => get(`/systems/${enc(id)}`),
  patchSystem: (id, p, expected_version) => patch(`/systems/${enc(id)}`, { patch: p, expected_version }),
  deleteSystem: (id) => request("DELETE", `/systems/${enc(id)}`),
  confirmSystem: (id) => post(`/systems/${enc(id)}/confirm`),
  confirmBulk: (body) => post("/systems/confirm-bulk", body),
  retriageSystem: (id) => post(`/systems/${enc(id)}/triage`),
  setActionDone: (id, actionId, done) =>
    patch(`/systems/${enc(id)}/actions/${enc(actionId)}`, { done }),

  checkDraft: (description, builder_token, builder) =>
    post(
      "/check/draft",
      builder_token
        ? {
            description,
            builder_token,
            ...(builder?.name?.trim() ? { name: builder.name.trim() } : {}),
            ...(builder?.email?.trim() ? { email: builder.email.trim() } : {}),
          }
        : { description },
    ),
  checkAnswer: (checkId, answers) => post(`/check/${enc(checkId)}/answer`, { answers }),
  checkSave: (checkId) => post(`/check/${enc(checkId)}/save`),
  checkCardPath: (checkId) => `/check/${enc(checkId)}/card.docx`,

  survey: () => get("/survey"),
  surveyAnalyze: () => post("/survey/analyze"),
  surveyInvitation: () => post("/survey/invitation"),
  surveyAcceptProposal: (index, revision) => post("/survey/proposals/accept", { index, revision }),
  publicSurvey: (token) => get(`/public/survey/${enc(token)}`),
  submitSurvey: (token, response) => post(`/public/survey/${enc(token)}`, response),
  publicIntake: (token) => get(`/public/intake/${enc(token)}`),
  submitIntake: (token, body) => post(`/public/intake/${enc(token)}`, body),
  publicBuilder: (token) => get(`/public/builder/${enc(token)}`),

  literacy: () => get("/literacy"),
  literacyPlan: () => post("/literacy/plan"),
  publicTraining: (token) => get(`/public/training/${enc(token)}`),
  checkTrainingAnswer: (token, body) => post(`/public/training/${enc(token)}/check`, body),
  submitTraining: (token, body) => post(`/public/training/${enc(token)}`, body),

  documents: () => get("/documents"),
  docDetails: () => get("/documents/details"),
  saveDocDetails: (values) => request("PUT", "/documents/details", values),
  suggestDocDetails: () => post("/documents/details/suggest"),
  policyAction: (body) => post("/documents/policy", body),
  confirmCard: (id, role, confirmed) => post(`/systems/${enc(id)}/card-confirm`, { role, confirmed }),
  download: async (path, filename) => {
    // Check first so that a 401 or a missing file gives a clear message instead of a broken tab.
    const res = await fetch(BASE + path, { credentials: "include" });
    if (!res.ok) throw await errorFromResponse(res);
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    // Prefer the UTF-8 name (filename*=, keeps č/š/ž); the plain filename= is an ASCII fallback.
    const star = /filename\*\s*=\s*(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
    let name = filename;
    if (star) {
      try {
        name = decodeURIComponent(star[1].trim());
      } catch {
        name = plain ? plain[1] : filename;
      }
    } else if (plain) name = plain[1];
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  },

  adminOrgs: () => get("/admin/orgs"),
  adminAccess: () => get("/admin/access"),
  adminAccessAdd: (entry, note) => post("/admin/access", note ? { entry, note } : { entry }),
  adminAccessRemove: (entry) => request("DELETE", `/admin/access?entry=${enc(entry)}`),

  chatHistory: () => get("/chat"),

  chat: async (message, onEvent, signal, picks) => {
    const res = await fetch(BASE + "/chat", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      // a picker answer goes as catalog_picks (the backend adds them and writes the user's line);
      // the message then holds the tools that are not in the catalog
      body: JSON.stringify(picks ? { message, catalog_picks: picks } : { message }),
      signal,
    });
    if (!res.ok) throw await errorFromResponse(res);
    await readSse(res, onEvent);
  },
};
